import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { 
  Receipt, 
  CreditCard, 
  Gift, 
  ExternalLink, 
  Search, 
  User, 
  Calendar, 
  DollarSign, 
  HelpCircle, 
  AlertCircle, 
  CheckCircle2 
} from "lucide-react";
import { toast } from "sonner";

const NotaFiscalPaulistaPage = () => {
  const [cpf, setCpf] = useState("");
  const [senha, setSenha] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (!cpf || !senha) {
      toast.error("Por favor, preencha todos os campos");
      return;
    }
    
    setLoading(true);
    
    // Simular login (mockado)
    setTimeout(() => {
      setLoading(false);
      toast.success("Login simulado com sucesso. Esta é apenas uma demonstração.");
    }, 1500);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    toast.info(`Pesquisa realizada por: ${searchQuery || "termo vazio"}`);
    setSearchQuery("");
  };

  const faqItems = [
    {
      question: "O que é a Nota Fiscal Paulista?",
      answer: "A Nota Fiscal Paulista é um programa de estímulo à cidadania fiscal no Estado de São Paulo, que gera créditos aos consumidores que solicitam a inclusão do CPF nas notas fiscais de suas compras."
    },
    {
      question: "Como faço para participar?",
      answer: "Para participar, basta informar o seu CPF no momento da compra. O estabelecimento comercial irá incluir essa informação na nota fiscal, gerando créditos para você."
    },
    {
      question: "Quanto tempo leva para os créditos aparecerem?",
      answer: "Os créditos são liberados em até 30 dias após a emissão da nota fiscal. O valor pode ser consultado no site ou aplicativo da Nota Fiscal Paulista."
    },
    {
      question: "Como resgatar os créditos?",
      answer: "Os créditos podem ser transferidos para conta bancária, utilizados para abater o valor do IPVA ou doados a entidades sociais cadastradas no programa."
    },
    {
      question: "Quais documentos preciso para cadastro?",
      answer: "Para se cadastrar, você precisará de CPF, data de nascimento, e-mail e criar uma senha. Para resgatar os créditos, será necessário informar dados bancários."
    }
  ];

  const newsItems = [
    {
      title: "Sorteio da Nota Fiscal Paulista libera R$ 25 milhões em prêmios",
      date: "10/07/2025",
      description: "O programa da Nota Fiscal Paulista realizará novo sorteio com valores de até R$ 1 milhão para consumidores cadastrados."
    },
    {
      title: "Aplicativo da Nota Fiscal Paulista ganha nova atualização",
      date: "05/07/2025",
      description: "Nova versão traz melhorias na interface e facilita o processo de consulta de créditos e transferência para conta bancária."
    },
    {
      title: "Prazo para resgate de créditos antigos termina em agosto",
      date: "01/07/2025",
      description: "Consumidores têm até 31 de agosto para resgatar créditos liberados há mais de 12 meses."
    }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main className="container mx-auto px-4 pt-24 pb-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent py-2">
            Nota Fiscal Paulista
          </h1>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
            Consulte seus créditos, participe de sorteios e aprenda como utilizar o programa da Nota Fiscal Paulista.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Receipt className="mr-2 h-5 w-5" /> Acesso ao Sistema
              </CardTitle>
              <CardDescription>Faça login para acessar seus créditos e informações</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-6">
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="cpf">CPF</Label>
                    <Input 
                      id="cpf"
                      type="text"
                      placeholder="Digite seu CPF (apenas números)"
                      value={cpf}
                      onChange={(e) => setCpf(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="senha">Senha</Label>
                    <Input 
                      id="senha"
                      type="password"
                      placeholder="Digite sua senha"
                      value={senha}
                      onChange={(e) => setSenha(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    type="submit" 
                    className="bg-primary hover:bg-primary/90 text-primary-foreground flex-grow"
                    disabled={loading}
                  >
                    {loading ? "Processando..." : "Acessar Sistema"}
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => toast.info("Esta é uma página de demonstração. Para acesso real, visite o site oficial.")}
                  >
                    Esqueci a Senha
                  </Button>
                </div>
                
                <div className="text-center pt-2">
                  <Button 
                    variant="link" 
                    onClick={() => toast.info("Esta é uma página de demonstração. Para criar uma conta real, visite o site oficial.")}
                  >
                    Primeiro acesso? Cadastre-se
                  </Button>
                </div>
              </form>
            </CardContent>
            <CardFooter className="bg-muted/50 flex justify-center">
              <Button 
                asChild 
                variant="ghost"
                className="text-primary hover:text-primary/90"
              >
                <a 
                  href="https://www.nfp.fazenda.sp.gov.br/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center"
                >
                  <ExternalLink className="mr-2 h-4 w-4" /> 
                  Acessar Site Oficial
                </a>
              </Button>
            </CardFooter>
          </Card>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Pesquisa Rápida</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSearch} className="space-y-4">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input 
                      type="search"
                      placeholder="Buscar informações..."
                      className="pl-9"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                  >
                    Pesquisar
                  </Button>
                </form>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Links Úteis</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  asChild
                >
                  <a 
                    href="https://www.nfp.fazenda.sp.gov.br/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <User className="mr-2 h-4 w-4" /> Área do Consumidor
                  </a>
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  asChild
                >
                  <a 
                    href="https://www.nfp.fazenda.sp.gov.br/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <Calendar className="mr-2 h-4 w-4" /> Calendário de Sorteios
                  </a>
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  asChild
                >
                  <a 
                    href="https://www.nfp.fazenda.sp.gov.br/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <DollarSign className="mr-2 h-4 w-4" /> Resgate de Créditos
                  </a>
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  asChild
                >
                  <a 
                    href="https://www.nfp.fazenda.sp.gov.br/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <Gift className="mr-2 h-4 w-4" /> Doação de Créditos
                  </a>
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  asChild
                >
                  <a 
                    href="https://www.nfp.fazenda.sp.gov.br/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <HelpCircle className="mr-2 h-4 w-4" /> Perguntas Frequentes
                  </a>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        <Tabs defaultValue="como-funciona" className="mb-12">
          <TabsList className="grid grid-cols-3 md:w-[600px] mx-auto">
            <TabsTrigger value="como-funciona">Como Funciona</TabsTrigger>
            <TabsTrigger value="perguntas-frequentes">Perguntas Frequentes</TabsTrigger>
            <TabsTrigger value="noticias">Notícias</TabsTrigger>
          </TabsList>
          
          <TabsContent value="como-funciona" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Como Funciona a Nota Fiscal Paulista</CardTitle>
                <CardDescription>Entenda o processo em passos simples</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="flex flex-col items-center text-center p-4 bg-muted rounded-lg">
                    <div className="bg-primary/10 p-3 rounded-full mb-4">
                      <Receipt className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-medium mb-2">1. Solicite a Nota</h3>
                    <p className="text-sm text-muted-foreground">Informe seu CPF ao realizar compras em estabelecimentos no estado de São Paulo.</p>
                  </div>
                  
                  <div className="flex flex-col items-center text-center p-4 bg-muted rounded-lg">
                    <div className="bg-primary/10 p-3 rounded-full mb-4">
                      <User className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-medium mb-2">2. Cadastre-se</h3>
                    <p className="text-sm text-muted-foreground">Faça seu cadastro no site oficial da Nota Fiscal Paulista para acessar seus créditos.</p>
                  </div>
                  
                  <div className="flex flex-col items-center text-center p-4 bg-muted rounded-lg">
                    <div className="bg-primary/10 p-3 rounded-full mb-4">
                      <DollarSign className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-medium mb-2">3. Receba Créditos</h3>
                    <p className="text-sm text-muted-foreground">Receba de volta parte do ICMS pago nas suas compras na forma de créditos.</p>
                  </div>
                  
                  <div className="flex flex-col items-center text-center p-4 bg-muted rounded-lg">
                    <div className="bg-primary/10 p-3 rounded-full mb-4">
                      <CreditCard className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-medium mb-2">4. Resgate</h3>
                    <p className="text-sm text-muted-foreground">Transfira os créditos para sua conta bancária, use para abater IPVA ou doe para instituições.</p>
                  </div>
                </div>
                
                <Separator className="my-6" />
                
                <div className="space-y-4">
                  <h3 className="text-xl font-medium">Benefícios do Programa</h3>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="bg-muted p-4 rounded-lg flex">
                      <div className="mr-4">
                        <DollarSign className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-medium">Devolução de Impostos</h4>
                        <p className="text-sm text-muted-foreground">Receba de volta até 20% do ICMS recolhido pelo estabelecimento.</p>
                      </div>
                    </div>
                    
                    <div className="bg-muted p-4 rounded-lg flex">
                      <div className="mr-4">
                        <Gift className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-medium">Sorteios Mensais</h4>
                        <p className="text-sm text-muted-foreground">Concorra a prêmios de até R$ 1 milhão em sorteios mensais.</p>
                      </div>
                    </div>
                    
                    <div className="bg-muted p-4 rounded-lg flex">
                      <div className="mr-4">
                        <CheckCircle2 className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-medium">Combate à Sonegação</h4>
                        <p className="text-sm text-muted-foreground">Contribua para reduzir a sonegação fiscal no estado.</p>
                      </div>
                    </div>
                    
                    <div className="bg-muted p-4 rounded-lg flex">
                      <div className="mr-4">
                        <AlertCircle className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-medium">Conscientização</h4>
                        <p className="text-sm text-muted-foreground">Incentiva a população a exigir a emissão de notas fiscais.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="perguntas-frequentes" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Perguntas Frequentes</CardTitle>
                <CardDescription>Respostas para as dúvidas mais comuns sobre a Nota Fiscal Paulista</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {faqItems.map((item, index) => (
                  <div key={index} className="border border-border rounded-lg overflow-hidden">
                    <div className="bg-muted/50 p-4 font-medium flex items-start">
                      <HelpCircle className="h-5 w-5 mr-2 text-primary shrink-0 mt-0.5" />
                      <span>{item.question}</span>
                    </div>
                    <div className="p-4 text-muted-foreground">
                      {item.answer}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="noticias" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Últimas Notícias</CardTitle>
                <CardDescription>Atualizações recentes sobre o programa Nota Fiscal Paulista</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {newsItems.map((item, index) => (
                  <div key={index} className="border border-border rounded-lg overflow-hidden">
                    <div className="bg-muted/50 p-4">
                      <h3 className="font-medium">{item.title}</h3>
                      <p className="text-xs text-muted-foreground mt-1">{item.date}</p>
                    </div>
                    <div className="p-4 text-muted-foreground">
                      {item.description}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="text-center">
          <Button 
            asChild 
            size="lg"
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            <a 
              href="https://www.nfp.fazenda.sp.gov.br/" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <ExternalLink className="mr-2 h-5 w-5" /> Acessar Site Oficial da Nota Fiscal Paulista
            </a>
          </Button>
          <p className="mt-4 text-sm text-muted-foreground">
            Para acessar todos os recursos e funcionalidades, visite o site oficial da Secretaria da Fazenda de São Paulo.
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default NotaFiscalPaulistaPage;